'use client';
import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

import { Button, Typography } from 'lighthouse';
import { CacheOutlineLargeSVG, MyPlatesOutlineMdSVG } from 'lighthouse-icons';

import { ProfileDropdownProps } from '@/widgets/ProfileDropdown/models';

import { userBatteryRequests, userInquiry, userInspectionRequests, userOrders, userProfile, userVehiclePlates, userWallet } from '@/constants/routes';

import { BatteryIcon } from '../../icons/BatteryIcon';
import { OrdersIcon } from '../../icons/OrdersIcon';
import { VisitExpertIcon } from '../../icons/VisitExpertIcon';

import { ArrowLeft2, LogoutCurve, ProfileCircle, Wallet3 } from 'iconsax-react';

export const ProfileDropdown = ({ className, onClose, open, userProfileData, userProfileLoading, userFullName, logout }: ProfileDropdownProps) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const router = useRouter();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    if (!open) return null;

    return (
        <div
            ref={menuRef}
            className={`absolute left-2 mx-4 mt-2 hidden rounded-lg border border-gray-200 bg-white shadow-lg md:flex ${className || ''}`}>
            <div className='p-4'>
                <div
                    className='flex cursor-pointer items-center justify-between gap-x-4 border-0 border-b border-solid border-gray-300 p-2 pb-4'
                    onClick={() => router.push(userProfile)}>
                    <ProfileCircle color='currentcolor' size={54} className='text-gray-300' />
                    {userProfileLoading ? (
                        <div className='flex flex-col gap-y-3'>
                            <div className='h-3 w-20 animate-pulse rounded-lg bg-gray-200'></div>
                            <div className='h-3 w-20 animate-pulse rounded-lg bg-gray-200'></div>
                        </div>
                    ) : (
                        <div className='flex flex-col gap-y-2'>
                            <Typography variant='button' component='span' className='font-semibold'>
                                {userFullName}
                            </Typography>
                            <Typography variant='body2' component='span' className='text-gray-500'>
                                {userProfileData?.phoneNumber}
                            </Typography>
                        </div>
                    )}

                    <ArrowLeft2 color='currentcolor' size={16} className='mr-12 text-gray-400' />
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5 pt-3'>
                    <Button
                        variant='text'
                        className='flex cursor-pointer items-center gap-x-2 bg-transparent'
                        onClick={() => router.push(userWallet)}>
                        <Typography
                            variant='button'
                            className='flex items-start justify-center gap-x-3 stroke-gray-400 hover:fill-primary/70 hover:stroke-primary/70
                                hover:text-primary/70'>
                            <Wallet3 size={20} />
                            کیف پول
                        </Typography>
                    </Button>
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5'>
                    <Button
                        variant='text'
                        className='flex cursor-pointer items-center gap-x-2 bg-transparent'
                        onClick={() => router.push(userVehiclePlates)}>
                        <Typography variant='button' className='group flex items-start justify-center gap-x-3 hover:text-primary/70'>
                            <MyPlatesOutlineMdSVG className='size-5 text-neutral-400 group-hover:text-primary/70' />
                            پلاک‌‌های من
                        </Typography>
                    </Button>
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5'>
                    <Button
                        variant='text'
                        className='flex cursor-pointer items-center gap-x-2 bg-transparent'
                        onClick={() => router.push(userInspectionRequests)}>
                        <Typography variant='button' className='group flex items-start justify-center gap-x-3 hover:text-primary/70'>
                            <VisitExpertIcon className='size-5 text-neutral-400 group-hover:text-primary/70' />
                            کارشناسی‌های من
                        </Typography>
                    </Button>
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5'>
                    <Button
                        variant='text'
                        className={'flex cursor-pointer items-center gap-x-2 bg-transparent'}
                        onClick={() => router.push(userBatteryRequests)}>
                        <Typography variant='button' className='group flex items-start justify-center gap-x-3 hover:text-primary/70'>
                            <BatteryIcon className='size-5 text-neutral-400 group-hover:text-primary/70' />
                            سفارش‌های باتری‌ من
                        </Typography>
                    </Button>
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5'>
                    <Button
                        variant='text'
                        className='flex cursor-pointer items-center gap-x-2 bg-transparent'
                        onClick={() => router.push(userOrders)}>
                        <Typography variant='button' className='group flex items-start justify-center gap-x-3 hover:text-primary/70'>
                            <OrdersIcon className='size-5 text-neutral-400 group-hover:text-primary/70' />
                            سفارش‌های من
                        </Typography>
                    </Button>
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5'>
                    <Button
                        variant='text'
                        className='flex cursor-pointer items-center gap-x-2 bg-transparent'
                        onClick={() => router.push(userInquiry)}>
                        <Typography variant='button' className='group flex items-start justify-center gap-x-3 hover:text-primary/70'>
                            <CacheOutlineLargeSVG className='size-5 text-neutral-400 group-hover:text-primary/70' />
                            استعلام های من
                        </Typography>
                    </Button>
                </div>
                <div className='flex w-full items-start justify-start gap-x-3 p-1.5'>
                    <Button variant='text' className='flex cursor-pointer items-center gap-x-2 bg-transparent' onClick={logout}>
                        <Typography variant='button' className='flex items-start justify-center gap-x-3 stroke-error text-error'>
                            <LogoutCurve size={20} />
                            خروج
                        </Typography>
                    </Button>
                </div>
            </div>
        </div>
    );
};
